import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import { Shield, Rocket, Zap, Save, Activity, CheckSquare } from 'lucide-react';
import RockActivityCreator from './RockActivityCreator';
import RockTaskCreator from './RockTaskCreator';

const TRACK_STATUS_OPTIONS = [
{ value: 'not_started', label: 'Not Started' },
{ value: 'in_progress', label: 'In Progress' },
{ value: 'completed', label: 'Completed' }];


const TRACK2_APPROACHES = [
  { value: 'build_internally', label: 'Build Internally' },
  { value: 'contract_service', label: 'Contract as Service' },
  { value: 'acquire', label: 'Acquire' },
  { value: 'build_alliance', label: 'Build Alliances' }
];

const TRACK3_APPROACHES = [
  { value: 'maneuver_around', label: 'Maneuver Around' },
  { value: 'retreat', label: 'Retreat / Pull Back' },
  { value: 'support_capability_decision', label: 'Support Capability Decision' }
];


function TrackSection({ title, icon: Icon, accentColor, children }) {
  const borderColor = accentColor === 'blue' ? 'border-blue-500/30' : accentColor === 'green' ? 'border-emerald-500/30' : 'border-amber-500/30';
  const bgColor = accentColor === 'blue' ? 'bg-blue-950/20' : accentColor === 'green' ? 'bg-emerald-950/20' : 'bg-amber-950/20';
  const textColor = accentColor === 'blue' ? 'text-blue-400' : accentColor === 'green' ? 'text-emerald-400' : 'text-amber-400';

  return (
    <div className="bg-zinc-100 p-4 rounded-lg border border-blue-500/30 space-y-3">
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${textColor}`} />
        <h4 className={`text-sm font-bold ${textColor}`}>{title}</h4>
      </div>
      {children}
    </div>);

}

function StatusBadge({ status }) {
  const colors = {
    not_started: 'bg-slate-500/20 text-slate-400',
    in_progress: 'bg-amber-500/20 text-amber-400',
    completed: 'bg-emerald-500/20 text-emerald-400'
  };
  const labels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed'
  };
  return <Badge className={colors[status] || colors.not_started}>{labels[status] || 'Not Started'}</Badge>;
}

export default function RockResolutionPanel({ issue, officers, policies, capabilities, onCreatePolicy, onCreateCapability, onComplete }) {
  const queryClient = useQueryClient();
  const [showActivityCreator, setShowActivityCreator] = useState(false);
  const [showTaskCreator, setShowTaskCreator] = useState(false);

  const [formData, setFormData] = useState({
    rock_track1_owner: issue?.rock_track1_owner || '',
    rock_track1_status: issue?.rock_track1_status || 'not_started',
    rock_track1_notes: issue?.rock_track1_notes || '',
    rock_track2_owner: issue?.rock_track2_owner || '',
    rock_track2_status: issue?.rock_track2_status || 'not_started',
    rock_track2_notes: issue?.rock_track2_notes || '',
    rock_track2_pathway: issue?.rock_track2_pathway || '',
    rock_track3_owner: issue?.rock_track3_owner || '',
    rock_track3_status: issue?.rock_track3_status || 'not_started',
    rock_track3_notes: issue?.rock_track3_notes || '',
    decision: issue?.decision || ''
  });

  const linkedPolicy = issue?.linked_policy_id ?
  policies?.find((p) => p.id === issue.linked_policy_id) :
  policies?.find((p) => (p.linked_issue_ids || []).includes(issue?.id));

  const linkedCapability = issue?.linked_capability_id ?
  capabilities?.find((c) => c.id === issue.linked_capability_id) :
  capabilities?.find((c) => (c.linked_issue_ids || []).includes(issue?.id));

  const saveMutation = useMutation({
    mutationFn: (data) => base44.entities.Issue.update(issue.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      onComplete?.();
    }
  });

  const officerOptions = (officers || []).map((o) => ({
    value: o.user_email,
    label: `${o.callsign} (${o.rank_title})`
  }));

  const handleSave = () => {
    const dataToSave = { ...formData };
    if (formData.rock_track2_pathway && formData.decision) {
      dataToSave.status = 'decision_made';
    }
    saveMutation.mutate(dataToSave);
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="text-stone-600 text-lg font-bold">🪨 Rock 3-Track Resolution</h3>
        <p className="text-xs text-slate-500 mt-1">Three parallel tracks launched simultaneously</p>
      </div>

      {/* TRACK 1: Bridging Policy */}
      <TrackSection title="Track 1: Bridging Policy" icon={Shield} accentColor="blue">
        <p className="text-slate-600 text-xs">What guardrails do we need right now to prevent further damage while we work on a permanent solution?</p>
        
        {linkedPolicy ?
        <div className="p-2 rounded border border-blue-500/20 bg-blue-950/30">
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-500/20 text-blue-400 text-xs">Linked Policy</Badge>
              <span className="text-sm text-blue-300">{linkedPolicy.title}</span>
            </div>
            <StatusBadge status={linkedPolicy.status} />
          </div> :

        <Button data-testid="issues-create-bridging-policy-btn" size="sm" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" onClick={() => onCreatePolicy?.(issue.id)}>
            <Shield className="w-3.5 h-3.5 mr-1" /> Create Bridging Policy
          </Button>
        }

        <div className="space-y-2">
          <Label className="text-blue-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Track Owner</Label>
          <SearchableTagSelect
            options={officerOptions}
            value={formData.rock_track1_owner}
            onValueChange={(v) => setFormData((p) => ({ ...p, rock_track1_owner: v }))}
            placeholder="Assign owner..."
            emptyMessage="No officers." />

        </div>
        <div className="space-y-2">
          <Label className="text-blue-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Status</Label>
          <Select value={formData.rock_track1_status} onValueChange={(v) => setFormData((p) => ({ ...p, rock_track1_status: v }))}>
            <SelectTrigger className="bg-slate-950 border-blue-500/20 h-8 text-sm text-slate-50"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRACK_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-blue-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Notes</Label>
          <Textarea data-testid="issues-track-1-notes-textarea" value={formData.rock_track1_notes} onChange={(e) => setFormData((p) => ({ ...p, rock_track1_notes: e.target.value }))} className="bg-slate-950 border-blue-500/20 text-slate-200 text-sm" rows={2} placeholder="Track 1 notes..." />
        </div>
      </TrackSection>

      {/* TRACK 2: Capability Acquisition */}
      <TrackSection title="Track 2: Capability Acquisition" icon={Rocket} accentColor="green">
        <p className="text-xs text-slate-400">What capability do we need and what's the fastest way to get it?</p>

        <div className="space-y-2">
          <Label className="text-emerald-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Approach</Label>
          <Select value={formData.rock_track2_pathway} onValueChange={(v) => setFormData((p) => ({ ...p, rock_track2_pathway: v }))}>
            <SelectTrigger className="bg-slate-950 border-emerald-500/20 h-8 text-sm text-slate-50"><SelectValue placeholder="Select approach..." /></SelectTrigger>
            <SelectContent>
              {TRACK2_APPROACHES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {linkedCapability ?
        <div className="p-2 rounded border border-emerald-500/20 bg-emerald-950/30">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Linked Capability</Badge>
              <span className="text-sm text-emerald-300">{linkedCapability.name}</span>
            </div>
            <StatusBadge status={linkedCapability.status} />
          </div> :

        <Button data-testid="issues-request-capability-btn" size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" onClick={() => onCreateCapability?.(issue.id)}>
            <Rocket className="w-3.5 h-3.5 mr-1" /> Request Capability
          </Button>
        }

        <div className="space-y-2">
          <Label className="text-emerald-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Track Owner</Label>
          <SearchableTagSelect
            options={officerOptions}
            value={formData.rock_track2_owner}
            onValueChange={(v) => setFormData((p) => ({ ...p, rock_track2_owner: v }))}
            placeholder="Assign owner..."
            emptyMessage="No officers." />

        </div>
        <div className="space-y-2">
          <Label className="text-emerald-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Status</Label>
          <Select value={formData.rock_track2_status} onValueChange={(v) => setFormData((p) => ({ ...p, rock_track2_status: v }))}>
            <SelectTrigger className="bg-slate-950 border-emerald-500/20 h-8 text-sm text-slate-50"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRACK_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-emerald-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Notes</Label>
          <Textarea data-testid="issues-track-2-notes-textarea" value={formData.rock_track2_notes} onChange={(e) => setFormData((p) => ({ ...p, rock_track2_notes: e.target.value }))} className="bg-slate-950 border-emerald-500/20 text-slate-200 text-sm" rows={2} placeholder="Track 2 notes..." />
        </div>
      </TrackSection>

      {/* TRACK 3: Operational Response */}
      <TrackSection title="Track 3: Operational Response" icon={Zap} accentColor="amber">
        <p className="text-xs text-slate-400">What can we do right now, with what we have, to mitigate the impact?</p>

        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Approach</Label>
          <Select value={formData.decision} onValueChange={(v) => setFormData((p) => ({ ...p, decision: v }))}>
            <SelectTrigger className="bg-slate-950 border-amber-500/20 h-8 text-sm text-slate-50"><SelectValue placeholder="Select approach..." /></SelectTrigger>
            <SelectContent>
              {TRACK3_APPROACHES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Track Owner</Label>
          <SearchableTagSelect
            options={officerOptions}
            value={formData.rock_track3_owner}
            onValueChange={(v) => setFormData((p) => ({ ...p, rock_track3_owner: v }))}
            placeholder="Assign owner..."
            emptyMessage="No officers." />

        </div>
        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Status</Label>
          <Select value={formData.rock_track3_status} onValueChange={(v) => setFormData((p) => ({ ...p, rock_track3_status: v }))}>
            <SelectTrigger className="bg-slate-950 border-amber-500/20 h-8 text-sm text-slate-50"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRACK_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Notes</Label>
          <Textarea data-testid="issues-track-3-notes-textarea" value={formData.rock_track3_notes} onChange={(e) => setFormData((p) => ({ ...p, rock_track3_notes: e.target.value }))} className="bg-slate-950 border-amber-500/20 text-slate-200 text-sm" rows={2} placeholder="Track 3 notes..." />
        </div>
        <div className="flex gap-2">
          <Button data-testid="issues-create-activity-btn" size="sm" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" onClick={() => setShowActivityCreator(true)}>
            <Activity className="w-3.5 h-3.5 mr-1" /> Create Activity
          </Button>
          <Button data-testid="issues-create-task-btn" size="sm" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" onClick={() => setShowTaskCreator(true)}>
            <CheckSquare className="w-3.5 h-3.5 mr-1" /> Create Task
          </Button>
        </div>
      </TrackSection>

      <Button onClick={handleSave} disabled={saveMutation.isPending} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
        <Save className="w-4 h-4 mr-2" />
        {saveMutation.isPending ? 'Saving...' : 'Save All Tracks'}
      </Button>

      <RockActivityCreator
        open={showActivityCreator}
        onClose={() => setShowActivityCreator(false)}
        issueId={issue?.id}
        officers={officers}
      />

      <RockTaskCreator
        open={showTaskCreator}
        onClose={() => setShowTaskCreator(false)}
        issueId={issue?.id}
        officers={officers}
      />
    </div>);

}